import {
  Divider,
  Hidden,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
} from '@mui/material';
import {
  GenUnitCard,
  CollapsibleTable,
  WorkOrderStepper,
  GenSnackBar,
} from '@pinnacleautomotive/clarity-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import carPlaceholder from '../../../assets/carPlaceholder.png';
import Empty from '../../../assets/empty.png';
import { StatusCode } from '../../../common/enums/enums';
import ServicesDetails from '../../../components/ServicesDetails';
import { hasDealerShipManagerRole } from '../../../helpers/users';
import {
  cancelStepChangeStatusAllOrders,
  getAllOrdersWithAllFilters,
  hideCancelConfirmation,
  setAllOrdersSortDetails,
  setAllOrdersStatus,
} from '../../../redux/actions/workQueue/allOrdersActions';
import { useGetVehiclePhotoCountMutation } from '../../../redux/rtk/vendors.services';
import { selectCurrentUserRole } from '../../../redux/selectors/userDataSelectors';
import {
  isChangeStepStatusApiLoading,
  selectAllOrders,
  selectCurrentPage,
  selectHasMore,
  selectIsLoading,
  selectShowCancelError,
  selectShowCancelSuccesful,
  allOrdersStatus,
  allOrdersSortEnabled,
  allOrdersSortBy,
  allOrdersSortField,
} from '../../../redux/selectors/workQueue/allOrdersSelectors';
import { CommonColumns } from '../commons';
import EmptyState from '../components/EmptyState';

const AllOrders = ({ personId, companyId, filters, isDoneTab }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation('orders');
  const orders = useSelector(selectAllOrders);
  const theme = useTheme();
  const mediumScreen = useMediaQuery(theme.breakpoints.up('md'));
  const orderStatus = useSelector(allOrdersStatus);
  const hasMore = useSelector(selectHasMore);
  const loading = useSelector(selectIsLoading);
  const currentPage = useSelector(selectCurrentPage);
  const userRoles = useSelector(selectCurrentUserRole);
  const showCancelSuccesSnackBar = useSelector(selectShowCancelSuccesful);
  const showCancelErrorSnackBar = useSelector(selectShowCancelError);
  const isChangeStepOrderApiLoading = useSelector(isChangeStepStatusApiLoading);
  const [errorSnackBar, setErrorSnackBar] = useState(false);
  const [sortFields, setSortFields] = useState({});
  const isSortEnabled = useSelector(allOrdersSortEnabled);
  const sortField = useSelector(allOrdersSortField);
  const sortColumnBy = useSelector(allOrdersSortBy);
  const errorStatus =
    orderStatus === StatusCode.BAD_REQUEST ||
    orderStatus === StatusCode.INTERNAL_SERVER_ERROR ||
    orderStatus === StatusCode.INTERNAL_ERROR;
  const [photoCount, setPhotoCount] = useState(null);
  const [putVehicle, { isLoading: isLoadingVehicle }] =
    useGetVehiclePhotoCountMutation();

  const filterCleanup = fullFilters => {
    const obj = { ...fullFilters };
    obj.dateRange = fullFilters.dateRange.formattedDate;
    return obj;
  };

  useEffect(() => {
    if (errorStatus) {
      setErrorSnackBar(true);
      dispatch(setAllOrdersStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, orderStatus]);

  useEffect(() => {
    dispatch(
      getAllOrdersWithAllFilters({
        companyId,
        pageNumber: 1,
        filters: filterCleanup(filters),
        isDoneTab,
        isSortEnabled,
        sortBy: sortColumnBy,
        sortField,
      })
    );
    dispatch(hideCancelConfirmation());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId, companyId, dispatch, filters, isDoneTab, isSortEnabled]);

  const handleCancelStatus = ({ vendorOrderId, cancellingNote }) => {
    dispatch(
      cancelStepChangeStatusAllOrders({ vendorOrderId, cancellingNote })
    );
  };

  const loadMoreData = () => {
    if (errorStatus) return;
    dispatch(
      getAllOrdersWithAllFilters({
        companyId,
        pageNumber: currentPage + 1,
        filters: filterCleanup(filters),
        isDoneTab,
        isSortEnabled,
        sortBy: sortColumnBy,
        sortField,
      })
    );
  };

  const dispatchHideCancelConfirmation = () => {
    dispatch(hideCancelConfirmation());
  };

  const handleColumnSort = (fieldValue, sortBy) => {
    const action = sortBy?.toLowerCase();
    const sortData = {};
    const currentAction = sortFields?.[fieldValue]?.[action];
    sortData[fieldValue] = {
      asc: false,
      desc: false,
      [action]: !currentAction,
    };
    setSortFields(sortData);
    if (sortData?.[fieldValue]?.[action]) {
      dispatch(
        getAllOrdersWithAllFilters({
          companyId,
          pageNumber: 1,
          filters: filters,
          isDoneTab,
          isSortEnabled: true,
          sortBy: action,
          sortField: fieldValue,
        })
      );
      dispatch(
        setAllOrdersSortDetails({
          sortEnabled: true,
          sortBy: action,
          sortField: fieldValue,
        })
      );
    } else {
      dispatch(
        getAllOrdersWithAllFilters({
          companyId,
          pageNumber: 1,
          filters: filters,
          isDoneTab,
          isSortEnabled: false,
        })
      );
      dispatch(
        setAllOrdersSortDetails({
          sortEnabled: false,
        })
      );
    }
  };

  const columns = CommonColumns(t, mediumScreen, [], isDoneTab, false); // temporarily disable sort for status

  const handleApi = async row => {
    if (!row?.vehicle?.vin) return;
    const body = {
      orderId: row?.orderId,
      dealerId: row?.dealer?.companyId,
    };
    try {
      const data = await putVehicle({
        vinOrStockNumber: row?.vehicle?.vin,
        body,
      }).unwrap();
      const enabledCount = row?.vehicle?.showPhotoCount;
      const photoCountValue = enabledCount ? data?.photosCount || 0 : null;
      setPhotoCount(prev => ({ ...prev, [row.orderId]: photoCountValue }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error in fetching photo count', err);
      setPhotoCount(prev => ({ ...prev, [row.orderId]: null }));
    }
  };

  return (
    <>
      <GenSnackBar
        title={t('getOrders.description')}
        open={errorSnackBar}
        type="error"
        onClose={() => setErrorSnackBar(false)}
      />
      <GenSnackBar
        title={t('cancelConfirmation')}
        open={showCancelSuccesSnackBar}
        onClose={() => dispatchHideCancelConfirmation()}
      />
      <GenSnackBar
        title={t('cancelError')}
        open={showCancelErrorSnackBar}
        type="error"
        onClose={() => dispatchHideCancelConfirmation()}
      />
      <CollapsibleTable
        columns={columns}
        rows={orders}
        hasNextPage={hasMore}
        loading={loading}
        onLoadMore={() => loadMoreData()}
        handleFieldSort={handleColumnSort}
        sortFields={sortFields}
        handleApi={handleApi}
        onClick={workOrder =>
          history.push({
            pathname: `/orders/${workOrder.vendorOrderId}`,
          })
        }
        collpasibleGetter={data => (
          <Box display="grid">
            <Box p={2} order={{ md: 2 }}>
              <Typography variant="subtitle2" color="textPrimary">
                {t('processStep.title')}
              </Typography>
            </Box>
            <Box p={2} mb={1} order={{ md: 3 }}>
              <WorkOrderStepper
                isVendor={false}
                isManager={hasDealerShipManagerRole([userRoles.roleName])}
                vendorOrderId={data.vendorOrderId}
                steps={data.workFlowHistory}
                statusCancel={handleCancelStatus}
                isApiLoading={isChangeStepOrderApiLoading}
                detail={true}
              />
            </Box>
            <Box>
              <Hidden mdUp>
                <Divider />
              </Hidden>
              <GenUnitCard
                order={data}
                image={carPlaceholder}
                showStockAndWoNumber={false}
              >
                <ServicesDetails
                  data={data}
                  isLoadingVehicle={isLoadingVehicle}
                  photoCount={photoCount}
                  showPhotoCount={true}
                />
              </GenUnitCard>
            </Box>
          </Box>
        )}
      />
      <EmptyState
        show={!loading && !orders.length}
        img={Empty}
        title={t('global:noWO')}
        subtitle={t('global:noWOAllOrders')}
      />
    </>
  );
};

export default AllOrders;
